/* eslint-disable */
import {
  Archive,
  AssignmentReturn,
  AssignmentTurnedIn,
  ChevronLeft,
  Delete,
  MoreVert,
  Refresh,
  Sync,
  WarningSharp,
} from '@material-ui/icons';
import FilterableTable from '../../components/FilterableTable/FilterableTable';
import {
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import moment from 'moment-timezone';
import { convertConstantLiteralToNormal, formatDate, getUserDisplayName } from '../../utils/string.helpers';
import { ItemReceipt, RootState } from '../../store/config/types';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { ThunkDispatch } from 'redux-thunk';
import { setToast } from '../../store/actions/toast.actions';
import { connect, ConnectedProps } from 'react-redux';
import { ItemReceiptService } from '../../services/warehouseWork.service';
import Card from '../../components/Card/Card';
import CardHeader from '../../components/Card/CardHeader';
import CardBody from '../../components/Card/CardBody';
import { useHistory } from 'react-router-dom';
import { fetchWarehouses } from '../../store/actions/warehouses.actions';
import WarehouseEditLink from '../../components/WarehouseEditLink/WarehouseEditLink';
import ItemReceiptInfo from './ItemReceiptInfo';
import StatusChip from '../../components/StatusChip/StatusChip';

const REFRESH_MILISECONDS = 60000;

const mapStateToProps = (state: RootState) => {
  return {
    auth: state.auth,
    loggedIn: state.auth.loggedIn,
    warehouses: state.warehouses?.warehouses,
  };
};
const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, any, any>) => ({
  setToast: (message: string, messageType: string) => dispatch(setToast(message, messageType)),
  fetchWarehouses: (shipperId: number) => dispatch(fetchWarehouses(shipperId)),
});
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

function ItemReceiptManagementComponent({ auth, warehouses, fetchWarehouses, setToast }: PropsFromRedux) {
  const [itemReceipts, setItemReceipts] = useState<ItemReceipt[]>([]);
  // const [tab, setTab] = useState<'ITEMS' | 'PENDING'>('PENDING');
  const [currentIR, setCurrentIR] = useState<ItemReceipt | null>(null);
  const [menuIR, setMenuIR] = useState<ItemReceipt | null>(null);
  const [loading, setLoading] = useState(false);
  const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout>();
  const [lastRefetch, setLastRefetch] = useState<Date>(new Date());
  const [date, setDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [searchText, setSearchText] = useState<string>('');
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    timerRef.current = setTimeout(refetchData, REFRESH_MILISECONDS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (date) setNeedsRefresh(true);
  }, [date]);

  useEffect(() => {
    if (!warehouses?.length && auth?.account?.shipper?.shipperId) {
      fetchWarehouses(auth?.account?.shipper?.shipperId);
    }
  }, [fetchWarehouses, warehouses, auth]);

  useEffect(() => {
    if (needsRefresh) {
      refetchData();
    }
  }, [needsRefresh]);

  function refetchData() {
    setNeedsRefresh(false);
    if (!loading) {
      ItemReceiptService.fetchItemReceipts()
        .then((response) => {
          const receipts: { [key: string]: ItemReceipt[] } = { PROCESSING: [], PENDING: [], ITEMS: [] };
          response.forEach((itemReceipt) => {
            if (['PROCESSING'].includes(itemReceipt.status ?? '')) {
              receipts.PROCESSING.push(itemReceipt);
            } else if (['PENDING_APPROVAL', 'SYNC_FAILED'].includes(itemReceipt.status ?? '')) {
              receipts.PENDING.push(itemReceipt);
            } else {
              receipts.ITEMS.push(itemReceipt);
            }
          });
          const allReceipts = [...receipts.PROCESSING, ...receipts.PENDING, ...receipts.ITEMS];
          setItemReceipts(allReceipts);
          setLastRefetch(new Date());
          setNeedsRefresh(false);
          setCurrentIR((cIr) => allReceipts.find((ir) => ir.itemReceiptId === cIr?.itemReceiptId) ?? null);
        })
        .finally(() => {
          if (timerRef.current) {
            try {
              clearTimeout(timerRef.current);
              timerRef.current = setTimeout(() => refetchData(), REFRESH_MILISECONDS);
            } catch (err) {
              console.error(err);
            }
          }
        });
    }
  }
  function handleOperation(operation: string, rows: any[]): void {
    switch (operation) {
      case 'item-receipts-approve':
        const emptyLotIds: number[] = [];
        const IdsToApprove = (rows as any[])
          .map((r) => r.itemReceipt as ItemReceipt)
          .filter((ir) => {
            const lotIsOk = ir.items?.every((irt) => irt.lotId);
            if (!lotIsOk) emptyLotIds.push(ir.itemReceiptId);
            return ir.status == 'PENDING_APPROVAL' && lotIsOk;
          })
          .map((ir) => ir.itemReceiptId);
        if (emptyLotIds.length) {
          setToast('Some lot ids are empty', 'danger');
        }
        if (IdsToApprove.length) {
          setLoading(true);
          ItemReceiptService.approveItemReceipts(IdsToApprove).finally(() => {
            setLoading(false);
            setNeedsRefresh(true);
          });
        }
        break;
      case 'item-receipts-reject':
        const IdsToReject = (rows as any[])
          .map((r) => r.itemReceipt as ItemReceipt)
          .filter((ir) => ir.status == 'PENDING_APPROVAL')
          .map((ir) => ir.itemReceiptId);
        if (IdsToReject.length) {
          setLoading(true);
          ItemReceiptService.rejectItemReceipts(IdsToReject).finally(() => {
            setLoading(false);
            setNeedsRefresh(true);
          });
        }
        break;
      case 'item-receipts-try-sync':
        const IdsToRetrySync = (rows as any[])
          .filter((ir) => ['Sync Failed', 'Approved'].includes(ir.status))
          .map((ir) => ir.itemReceiptId);
        if (IdsToRetrySync.length) {
          setLoading(true);
          ItemReceiptService.tryToSyncItemReceipts(IdsToRetrySync).finally(() => {
            setLoading(false);
            setNeedsRefresh(true);
          });
        }
        break;
      case 'set-item-receipt-item-lot-id':
        ItemReceiptService.changeItemReceiptItemLot(rows[0]).finally(() => {
          setNeedsRefresh(true);
        });
        break;
      case 'set-item-receipt-warehouse':
        ItemReceiptService.changeItemReceiptWarehouse(rows[0]).finally(() => {
          setNeedsRefresh(true);
        });
        break;
      case 'set-item-receipt-vendor':
        ItemReceiptService.changeItemReceiptVendor(rows[0]).finally(() => {
          setNeedsRefresh(true);
        });
        break;
      case 'close-current-ir':
        if (currentIR) handleBackButtonClick();
        break;
      default:
        console.log(operation, rows);
    }
  }
  function handleBackButtonClick() {
    if (currentIR) {
      setCurrentIR(null);
    } else {
      history.goBack();
    }
  }

  function handleIRMenuClick(e: any, itemReceipt: ItemReceipt): void {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setMenuIR(itemReceipt);
  }
  function handleIRMenuClose() {
    setAnchorEl(null);
    setMenuIR(null);
  }
  return (
    <Card>
      <CardHeader color="primary">
        <Grid container p={0} m={0} alignItems="center">
          <Grid item flexGrow={1}>
            <IconButton size="small" onClick={handleBackButtonClick}>
              <ChevronLeft style={{ color: 'white' }} />
            </IconButton>
            {currentIR ? (
              currentIR.additionalData?.externalLink ? (
                <span style={{ marginRight: 16 }}>
                  <a
                    href={currentIR.additionalData.externalLink}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'white' }}
                  >
                    {currentIR.additionalData.label ?? currentIR.externalId ?? `#${currentIR.itemReceiptId}`}
                  </a>
                </span>
              ) : (
                <span>{`Item Receipt #${currentIR.itemReceiptId}`}</span>
              )
            ) : (
              <span>Item Receipts</span>
            )}
          </Grid>
          <Grid item alignItems="center">
            <Typography variant="caption">Last Update {moment(lastRefetch).format('HH:mm')}</Typography>
            <IconButton
              size="small"
              onClick={() => {
                if (!loading) setNeedsRefresh(true);
              }}
            >
              {loading ? (
                <CircularProgress size={24} style={{ color: 'white' }} />
              ) : (
                <Refresh style={{ color: 'white' }} />
              )}
            </IconButton>
          </Grid>
        </Grid>
      </CardHeader>
      <CardBody>
        {currentIR == null ? (
          <Grid container>
            {/* <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab value="ITEMS" label="Item Receipts" />
            <Tab value="PENDING" label="Pending Approval" />
          </Tabs> */}
            <FilterableTable
              onClickItem={(row) => {
                setCurrentIR(row.itemReceipt as ItemReceipt);
              }}
              // cumulativeOperations={[
              //   {
              //     operation: 'item-receipts-approve',
              //     caption: 'Approve Selection',
              //     icon: <AssignmentTurnedIn />,
              //     isDisabled(rows) {
              //       return rows.findIndex((ir) => ir.status == 'Pending Approval') < 0;
              //     },
              //   },
              //   {
              //     operation: 'item-receipts-reject',
              //     caption: 'Reject Selection',
              //     icon: <AssignmentReturn />,
              //     isDisabled(rows) {
              //       return rows.findIndex((ir) => ir.status == 'Pending Approval') < 0;
              //     },
              //   },
              //   {
              //     operation: 'item-receipts-try-sync',
              //     caption: 'Try to Sync',
              //     icon: <Sync />,
              //     isDisabled(rows) {
              //       return rows.findIndex((ir) => ['Sync Failed', 'Approved'].includes(ir.status)) < 0;
              //     },
              //   },
              // ]}
              columns={[
                {
                  field: 'id',
                  caption: 'ID',
                  filterType: 'string',
                  sortable: true,
                  render(row) {
                    let label: any = row.id;
                    if (row.additionalData?.externalLink) {
                      label = (
                        <a href={row.additionalData.externalLink} target="_blank" rel="noreferrer">
                          {row.additionalData.label ?? row.id}
                        </a>
                      );
                    }

                    return (
                      <Grid container gap={1} alignItems={'center'} flexWrap={'nowrap'}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleIRMenuClick(e, row.itemReceipt as ItemReceipt)}
                          disabled={['SYNCED'].includes(row.itemReceipt.status)}
                        >
                          <MoreVert />
                        </IconButton>
                        {label}
                      </Grid>
                    );
                  },
                  // render(row) {
                  //   const label = row.additionalData?.label ?? row.id;

                  //   return (
                  //     <a href={row.additionalData?.externalLink} target="_blank">
                  //       {label}
                  //     </a>
                  //   );
                  // },
                },
                {
                  field: 'receivedDate',
                  caption: 'Date',
                  sortable: true,
                  filterType: 'date',
                  render(row) {
                    return (
                      <Typography variant="body2" color={row.forPast ? 'brown' : undefined}>
                        <Grid container flexDirection="row" gap={1} alignItems={'center'} flexWrap={'nowrap'}>
                          {row.receivedDate}
                          {row.forPast && <WarningSharp style={{ height: 16 }} />}
                        </Grid>
                      </Typography>
                    );
                  },
                },
                {
                  field: 'warehouseName',
                  caption: 'Warehouse',
                  filterType: 'values',
                  render: (row) => (
                    <WarehouseEditLink
                      disabled={loading}
                      warehouseId={row.warehouseId}
                      warehouses={warehouses ?? []}
                      onSubmit={(warehouseId) =>
                        handleOperation('set-item-receipt-warehouse', [
                          {
                            warehouseId,
                            itemReceiptId: row.itemReceiptId,
                          },
                        ])
                      }
                    />
                  ),
                },
                {
                  field: 'vendor',
                  caption: 'Vendor',
                  filterType: 'values',
                  sortable: true,
                },
                {
                  field: 'humanizedStatus',
                  caption: 'Status',
                  filterType: 'values',
                  sortable: true,
                  render(row) {
                    return <StatusChip status={row.status} />;
                  },
                },
                {
                  field: 'warehouseUser',
                  caption: 'Created by',
                  filterType: 'values',
                },
                {
                  field: 'approvedBy',
                  caption: 'Approved by',
                  filterType: 'values',
                },
                {
                  field: 'comments',
                  caption: 'Comments',
                  filterType: 'string',
                },
              ]}
              defaultSortBy=""
              primaryKey="itemReceiptId"
              data={
                itemReceipts.map((ir) => {
                  moment.tz.setDefault(auth?.account?.timezone);
                  const forPast =
                    !ir.receivedDate ||
                    (moment().startOf('day').isAfter(ir.receivedDate) &&
                      ['PENDING', 'SYNC_FAILED'].includes(ir.status ?? ''));
                  return {
                    itemReceiptId: ir.itemReceiptId,
                    id: ir.externalId ?? `#${ir.itemReceiptId}`,
                    additionalData: ir.additionalData,
                    receivedDate: ir.receivedDate ? formatDate(ir.receivedDate) : '',
                    comments: ir.comments,
                    status: ir.status,
                    humanizedStatus: convertConstantLiteralToNormal(ir.status),
                    forPast,
                    itemReceipt: ir,
                    warehouseUser: `${ir.warehouseUser?.firstName ?? ''} ${ir.warehouseUser?.lastName ?? ''}`,
                    warehouseName: ir.warehouse?.name,
                    warehouseId: ir.warehouseId,
                    approvedBy: getUserDisplayName(ir.additionalData?.approvedBy),
                    vendor: ir.vendor,
                    vendorId: ir.vendorId,
                  };
                }) ?? []
              }
              onCumulativeOperation={handleOperation}
            />
          </Grid>
        ) : (
          <Grid container>
            <ItemReceiptInfo
              warehouses={warehouses ?? []}
              itemReceipt={currentIR}
              onOperation={handleOperation}
              disableActions={loading}
            />
          </Grid>
        )}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleIRMenuClose}>
          {menuIR?.status &&
            (!['SYNCED'].includes(menuIR.status) ? (
              <>
                <MenuItem onClick={() => handleOperation('inventory-count-order-cancel', [menuIR])}>
                  <ListItemIcon sx={{ color: 'error.main' }}>
                    <Delete />
                  </ListItemIcon>
                  Cancel
                </MenuItem>
              </>
            ) : (
              <>
                <MenuItem onClick={() => handleOperation('inventory-count-order-archive', [menuIR])}>
                  <ListItemIcon sx={{ color: 'success.main' }}>
                    <Archive />
                  </ListItemIcon>
                  Archive
                </MenuItem>
              </>
            ))}
        </Menu>
      </CardBody>
    </Card>
  );
}

const ItemReceiptManagement = connector(ItemReceiptManagementComponent);

export default ItemReceiptManagement;
